/**
 * intelligenceService.js — Central Intelligence Layer
 * ====================================================
 * Single source of truth for every piece of derived user health
 * intelligence in PCOS Insights. Every screen (Home · Cycle · Care ·
 * Theo · Notifications) should consume this instead of re-deriving
 * cycle phase, hormone patterns, symptom tags or confidence scores.
 *
 * Design rules:
 *   1. Pure functions have no I/O and are deterministic.
 *   2. `deriveUserIntelligence(userId)` is the ONLY async entry point.
 *      It parallelizes all Supabase reads via the existing
 *      `userDataService` helpers plus a few small direct queries.
 *   3. The output shape is stable and backwards-compatible with
 *      every consumer that previously computed its own tags.
 *   4. All hormone column-name compatibility (`testosteone` legacy
 *      column) is centralized in `normalizeHormones()`. Consumers
 *      should never OR-fallback the typo themselves.
 *
 * Consumers migrated to this service in Phase A:
 *   • CareScreen.jsx           (was: buildUserProfile)
 *   • theoService.js           (was: computePhase + detectPattern)
 *   • HomeScreen.jsx           (was: localStorage.pcosHormonePattern
 *                              + pcosSymptomPattern reads)
 *
 * Screens that intentionally keep their local derivations for now:
 *   • useCycleData / cycle sub-screens — cycle-specific rendering
 *     that already reads live from Supabase. Phase B may unify.
 */
import { supabase } from './supabase';
import {
  fetchOnboardingData,
  fetchCycleData,
  fetchHormoneData,
  fetchSymptomLogs,
  fetchTodayCheckin,
} from './userDataService';

/* ═══════════════════════════════════════════════════════════════
   PURE HELPERS — no I/O, deterministic, safe to unit-test
   ═══════════════════════════════════════════════════════════════ */

/** Cycle phase from cycle-day + length. Kept identical to
 *  theoService's previous implementation for regression safety. */
export const computePhase = (cycleDay, cycleLength = 28) => {
  if (cycleDay == null) return null;
  if (cycleDay <= 5) return 'menstrual';
  if (cycleDay <= Math.round(cycleLength * 0.45)) return 'follicular';
  if (cycleDay <= Math.round(cycleLength * 0.55)) return 'ovulation';
  return 'luteal';
};

/** CareScreen-style phase (uses ovulation-day = cycleLen − 14).
 *  Kept separately because CareScreen's phase names drive its
 *  content dictionary; unifying them risks a content mismatch. */
export const computeCarePhase = (cycleDay, cycleLength = 28) => {
  if (cycleDay == null) return 'general';
  const ovDay = cycleLength - 14;
  if (cycleDay <= 5) return 'menstrual';
  if (cycleDay <= ovDay - 2) return 'follicular';
  if (cycleDay <= ovDay + 2) return 'ovulation';
  return 'luteal';
};

/** Normalize hormone-report row into the canonical shape used by
 *  every consumer. Silently maps the legacy DB column `testosteone`
 *  onto the correct `testosterone` field. */
export const normalizeHormones = (row) => {
  if (!row) return null;
  const t = row.testosterone != null ? row.testosterone : row.testosteone;
  return {
    lh:           row.lh           != null ? row.lh           : null,
    fsh:          row.fsh          != null ? row.fsh          : null,
    insulin:      row.insulin      != null ? row.insulin      : null,
    testosterone: t != null ? t : null,
    amh:          row.amh          != null ? row.amh          : null,
  };
};

/** Hormone-pattern detection. Merges CareScreen's `buildUserProfile`
 *  and theoService's `detectPattern` into one canonical version. */
export const detectHormonePattern = (h) => {
  if (!h) return { tags: [], concerns: [], summary: null };
  const tags = [];
  const concerns = [];

  const lh  = parseFloat(h.lh);
  const fsh = parseFloat(h.fsh);
  const t   = parseFloat(h.testosterone);
  const ins = parseFloat(h.insulin);
  const amh = parseFloat(h.amh);

  if (lh && fsh && fsh > 0 && lh / fsh >= 2) {
    tags.push('elevated_lh_fsh');
    concerns.push('hormonal_imbalance');
  }
  if (t && t > 70) {
    tags.push('high_testosterone');
    concerns.push('acne_hormonal');
  }
  if (ins && ins > 12) {
    tags.push('insulin_resistant');
    concerns.push('insulin_resistant');
  }
  if (amh && amh > 3.5) {
    tags.push('elevated_amh');
  }

  const humanTags = [];
  if (tags.includes('elevated_lh_fsh'))    humanTags.push('elevated LH/FSH');
  if (tags.includes('high_testosterone'))  humanTags.push('elevated testosterone');
  if (tags.includes('insulin_resistant'))  humanTags.push('insulin resistance signal');
  if (tags.includes('elevated_amh'))       humanTags.push('elevated AMH');

  return {
    tags,
    concerns,
    summary: humanTags.length ? humanTags.join(', ') : null,
  };
};

/** Confidence scoring — driven by data freshness. Kept identical
 *  to theoService.calcConfidence. */
export const calcConfidence = ({ symptomsDaysAgo, hormonesDaysAgo, cycleDaysAgo }) => {
  let score = 0;
  if (symptomsDaysAgo != null && symptomsDaysAgo <= 2) score += 3;
  else if (symptomsDaysAgo != null && symptomsDaysAgo <= 7) score += 2;
  else if (symptomsDaysAgo != null && symptomsDaysAgo <= 21) score += 1;

  if (hormonesDaysAgo != null && hormonesDaysAgo <= 30) score += 3;
  else if (hormonesDaysAgo != null && hormonesDaysAgo <= 90) score += 2;
  else if (hormonesDaysAgo != null && hormonesDaysAgo <= 180) score += 1;

  if (cycleDaysAgo != null && cycleDaysAgo <= 7) score += 2;
  else if (cycleDaysAgo != null && cycleDaysAgo <= 30) score += 1;

  if (score >= 6) return 'high';
  if (score >= 3) return 'medium';
  return 'low';
};

const daysAgo = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

/** Assessment-driven tags. Pure function — takes an onboarding row
 *  (from `profiles`) and returns the semantic tags every intelligence
 *  surface can consume. Used by `buildUserTags` and exposed directly
 *  so callers (e.g. Theo backend context) can request them without
 *  paying for the rest of the intelligence pipeline. */
export const deriveAssessmentTags = (onboarding) => {
  if (!onboarding) return [];
  const tags = [];

  /* Primary goal */
  if (onboarding.primary_goal) {
    tags.push(`goal_${onboarding.primary_goal}`);
  }

  /* Diagnosis status */
  if (onboarding.diagnosis_status) {
    tags.push(`diagnosis_${onboarding.diagnosis_status}`);
    if (onboarding.diagnosis_status === 'suspected' || onboarding.diagnosis_status === 'exploring') {
      tags.push('assessment_exploring');
    }
    if (onboarding.diagnosis_status === 'diagnosed') {
      tags.push('assessment_diagnosed');
    }
  }

  /* Cycle regularity (assessment-declared, complements observed data) */
  if (onboarding.cycle_regularity) {
    tags.push(`regularity_${onboarding.cycle_regularity}`);
    if (['irregular', 'long', 'absent'].includes(onboarding.cycle_regularity)) {
      tags.push('assessment_irregular');
    }
  }

  /* Age bracket — coarse, never revealed in UI, drives copy only */
  const age = parseInt(onboarding.age, 10);
  if (age >= 13 && age <= 19)      tags.push('age_teen');
  else if (age >= 20 && age <= 29) tags.push('age_20s');
  else if (age >= 30 && age <= 39) tags.push('age_30s');
  else if (age >= 40)              tags.push('age_40plus');

  return tags;
};

/** Build the full symptom/tag profile — matches CareScreen's
 *  `buildUserProfile` output shape exactly so the consumer can drop
 *  in without any downstream changes. */
export const buildUserTags = ({ cycle, hormones, symptoms, checkins, onboarding, streak }) => {
  const profile = { tags: [], dominantConcerns: [], tone: 'supportive' };

  /* Cycle */
  if (cycle?.cycleLength > 35) profile.tags.push('irregular_cycle');
  if (cycle?.lastPeriodDate) {
    const days = Math.floor((new Date() - new Date(cycle.lastPeriodDate)) / 86400000);
    if (days > (cycle.cycleLength || 28) + 7) profile.tags.push('overdue_period');
  }

  /* Hormones — reuse the canonical detector */
  if (hormones) {
    const hp = detectHormonePattern(hormones);
    hp.tags.forEach(t => { if (!profile.tags.includes(t)) profile.tags.push(t); });
    hp.concerns.forEach(c => { if (!profile.dominantConcerns.includes(c)) profile.dominantConcerns.push(c); });
  }

  /* Symptom averages (7-day window) */
  if (Array.isArray(symptoms) && symptoms.length > 0) {
    const avgAcne     = symptoms.reduce((s, r) => s + (r.acne     || 0), 0) / symptoms.length;
    const avgFatigue  = symptoms.reduce((s, r) => s + (r.fatigue  || 0), 0) / symptoms.length;
    const avgMood     = symptoms.reduce((s, r) => s + (r.mood     || 0), 0) / symptoms.length;
    const avgHairLoss = symptoms.reduce((s, r) => s + (r.hair_loss|| 0), 0) / symptoms.length;

    if (avgFatigue  >= 2)   { profile.tags.push('fatigue_heavy'); profile.dominantConcerns.push('fatigue_heavy'); }
    if (avgAcne     >= 1.5) { profile.tags.push('acne_active');   profile.dominantConcerns.push('acne_hormonal'); }
    if (avgMood     >= 2)   { profile.tags.push('stressed');      profile.dominantConcerns.push('stressed'); }
    if (avgHairLoss >= 1.5) profile.tags.push('hair_loss_active');
  }

  /* Today's checkin */
  if (checkins?.mood >= 2) profile.tags.push('stressed_today');
  if (checkins?.energy_level <= 1 || checkins?.energy <= 1) profile.tags.push('low_energy_today');

  /* Streak / consistency */
  if (streak >= 7) { profile.tags.push('consistent_tracker'); profile.tone = 'celebratory'; }
  else if (streak >= 3)               profile.tone = 'motivational';
  else if (profile.dominantConcerns.length > 0) profile.tone = 'supportive';
  else                                profile.tone = 'educational';

  /* Assessment tags — merged in from the pure helper so both direct
     callers and the full intelligence pipeline stay in sync. */
  const assessmentTags = deriveAssessmentTags(onboarding);
  assessmentTags.forEach(t => { if (!profile.tags.includes(t)) profile.tags.push(t); });

  /* Assessment cross-reinforcement: user declared irregular cycles →
     add symptom-side concern so Care/Notifications can react even
     before enough logs exist to observe it. */
  if (assessmentTags.includes('assessment_irregular') && !profile.dominantConcerns.includes('irregular_cycle')) {
    profile.dominantConcerns.push('irregular_cycle');
  }
  /* If they've been formally diagnosed but hormone data hasn't arrived
     yet, keep tone supportive rather than educational. */
  if (assessmentTags.includes('assessment_diagnosed') && profile.tone === 'educational') {
    profile.tone = 'supportive';
  }

  return profile;
};

/* ═══════════════════════════════════════════════════════════════
   MAIN ENTRY POINT — async, fetches everything + derives
   ═══════════════════════════════════════════════════════════════ */

/**
 * Fetch every user signal in parallel and return the unified
 * intelligence object. Consumers should call this once per screen
 * (or per page load) and pass the result to their render logic.
 *
 * @param {string} userId
 * @param {object} [options]
 * @param {boolean} [options.withStreak=false] — include 30-day
 *   symptom-log streak (adds one extra Supabase query).
 */
export const deriveUserIntelligence = async (userId, options = {}) => {
  if (!userId) return null;
  const { withStreak = false } = options;

  const [
    onboarding,
    cycle,
    hormoneRaw,
    symptomLogs,
    todayCheckin,
    weekSymptoms,
    streakRows,
  ] = await Promise.all([
    fetchOnboardingData(userId),
    fetchCycleData(userId),
    fetchHormoneData(userId),
    fetchSymptomLogs(userId),
    fetchTodayCheckin(userId),
    _fetchRecentSymptoms(userId, 7),
    withStreak ? _fetchStreakDates(userId) : Promise.resolve(null),
  ]);

  const hormones = normalizeHormones(hormoneRaw);

  /* Cycle day + phase */
  let cycleDay = null;
  let phase = null;
  let carePhase = 'general';
  if (cycle?.lastPeriodDate && cycle?.cycleLength) {
    const lastPeriod = new Date(cycle.lastPeriodDate);
    const daysSince = Math.floor((new Date() - lastPeriod) / 86400000);
    const cycleLen = parseInt(cycle.cycleLength) || 28;
    cycleDay = (daysSince % cycleLen) + 1;
    phase = computePhase(cycleDay, cycleLen);
    carePhase = computeCarePhase(cycleDay, cycleLen);
  }

  /* Streak */
  const streak = withStreak ? _computeStreak(streakRows) : 0;

  /* Tags & profile */
  const profile = buildUserTags({
    cycle, hormones, symptoms: weekSymptoms, checkins: todayCheckin,
    onboarding, streak,
  });

  /* Hormone pattern for surfacing */
  const hormonePattern = detectHormonePattern(hormones);

  /* Assessment tags — surfaced at top level so consumers don't need
     to filter `profile.tags`. */
  const assessmentTags = deriveAssessmentTags(onboarding);

  /* Data confidence */
  const confidence = calcConfidence({
    symptomsDaysAgo:  weekSymptoms?.[0] ? daysAgo(weekSymptoms[0].log_date || weekSymptoms[0].checkin_date) : null,
    hormonesDaysAgo:  hormoneRaw?.created_at ? daysAgo(hormoneRaw.created_at) : null,
    cycleDaysAgo:     cycle?.updated_at ? daysAgo(cycle.updated_at) : null,
  });

  const dataAvailable = {
    cycle:    Boolean(cycle?.lastPeriodDate && cycle?.cycleLength),
    symptoms: Array.isArray(weekSymptoms) && weekSymptoms.length > 0,
    hormones: Boolean(hormones),
    onboarding: Boolean(onboarding),
  };

  return {
    userId,
    computedAt: new Date().toISOString(),

    /* Raw sources (already-normalized) */
    onboarding,
    cycle,
    hormones,
    symptomLogs,
    weekSymptoms,
    todayCheckin,

    /* Derived */
    phase,
    carePhase,
    cycleDay,
    streak,
    hormonePattern,      // { tags, concerns, summary }
    profile,             // { tags, dominantConcerns, tone }
    tags:               profile.tags,           // convenience alias
    assessmentTags,      // subset of tags: goal_·diagnosis_·regularity_·age_·assessment_*
    dominantConcerns:   profile.dominantConcerns,
    tone:               profile.tone,

    confidence,          // 'high' | 'medium' | 'low'
    dataAvailable,
  };
};

/* ═══════════════════════════════════════════════════════════════
   Internal helpers
   ═══════════════════════════════════════════════════════════════ */

const _fetchRecentSymptoms = async (userId, days) => {
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  const { data } = await supabase
    .from('symptom_logs')
    .select('*')
    .eq('user_id', userId)
    .gte('log_date', from)
    .order('log_date', { ascending: false });
  return data || [];
};

const _fetchStreakDates = async (userId) => {
  const { data } = await supabase
    .from('symptom_logs')
    .select('log_date')
    .eq('user_id', userId)
    .order('log_date', { ascending: false })
    .limit(30);
  return data || [];
};

const _computeStreak = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return 0;
  let checkDate = new Date();
  let streak = 0;
  for (const row of rows) {
    const expected = checkDate.toISOString().split('T')[0];
    if (row.log_date === expected) {
      streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else {
      break;
    }
  }
  return streak;
};
